import React, { CSSProperties, useMemo, useState, useEffect, useRef, useCallback } from 'react';
import DataTable from './DataTable';
import ColumnsOptionsSidebar, {
    ColumnsOptionsSidebarColumn,
    ColumnsOptionsSidebarColumnOrder
} from '../columnsOptionsSidebar/ColumnsOptionsSidebar';
import { DataTableColumn, ColumnWidth, OrderData, TableRow, InfiniteScrollData, ExpandableRowConfig, getColumnId } from './models';
import DataTableCache, { DEFAULT_TABLE_DATA } from './DataTableCache';
import { exportToExcel } from 'common/utils/exportToExcel';
import styles from './DataTableWithSidebar.module.scss';

interface Props<TRow> {
    cacheKey: string;
    columns: DataTableColumn<TRow>[];
    rows: TRow[];
    defaultOrder?: OrderData<TRow>;
    defaultHiddenColumns?: string[];
    disallowHover?: boolean;
    striped?: boolean;
    selectedRow?: TRow | null;
    showErrors?: boolean;
    errors?: any[];
    className?: string;
    hideHeaderWhenNoData?: boolean;
    colorRow?: string;
    noItemsText?: string;
    infiniteScroll?: InfiniteScrollData;
    scrollToRowId?: string | null;
    canDragColumns?: boolean;
    disableResize?: boolean;
    expandableRow?: ExpandableRowConfig<TRow>;
    onRowClick?: (row: TRow, index: number, event: any) => void;
    onOrderChange?: (orderData: OrderData<TRow>) => void;
    onHref?: (row: TRow) => string;
    rowStyle?: (row: TRow, col: DataTableColumn<TRow>, rowIndex: number, colIndex: number) => CSSProperties;
    onRenderAction?: (row: TRow) => JSX.Element | undefined;
    onRowId?: (row: TRow) => string;
    exportExcel?: {
        fetchAll: () => Promise<TRow[]>;
        fileName: string;
        customExport?: (context: { hiddenColumns: string[]; orderedFields: (keyof TRow)[] }) => Promise<void>;
    };
    exportRef?: React.MutableRefObject<{ exportExcel: () => Promise<void> } | null>;
    hideExportInSidebar?: boolean;
}

const DataTableWithSidebar = <TRow extends TableRow>({
    cacheKey,
    columns,
    rows,
    defaultOrder,
    defaultHiddenColumns = [],
    disallowHover = false,
    striped = false,
    selectedRow,
    showErrors = false,
    errors = [],
    className,
    hideHeaderWhenNoData,
    colorRow,
    noItemsText,
    infiniteScroll,
    scrollToRowId,
    canDragColumns = true,
    disableResize = false,
    expandableRow,
    onRowClick,
    onOrderChange,
    onHref,
    rowStyle,
    onRenderAction,
    onRowId,
    exportExcel,
    exportRef,
    hideExportInSidebar = false,
}: Props<TRow>) => {
    // Initialize state from cache or defaults
    const [tableData, setTableData] = useState(() => {
        const cached = DataTableCache.get<TRow, unknown>(cacheKey, {
            ...DEFAULT_TABLE_DATA,
            order: defaultOrder ?? { field: columns[0]?.field ?? 'id', isOrderAsc: true },
            hiddenColumns: defaultHiddenColumns,
        });
        return cached;
    });

    // Sync order with defaultOrder when it changes (e.g., when backend criteria change)
    useEffect(() => {
        if (defaultOrder) {
            const orderChanged =
                defaultOrder.field !== tableData.order.field ||
                defaultOrder.isOrderAsc !== tableData.order.isOrderAsc ||
                defaultOrder.orderColumn !== tableData.order.orderColumn;

            if (orderChanged) {
                setTableData(prev => ({ ...prev, order: defaultOrder }));
            }
        }
    }, [defaultOrder?.field, defaultOrder?.isOrderAsc, defaultOrder?.orderColumn]);

    // Save to cache when tableData changes
    useEffect(() => {
        DataTableCache.save(cacheKey, tableData);
    }, [cacheKey, tableData]);

    // Apply column ordering
    const orderedColumns = useMemo(() => {
        if (!tableData.columnsOrder || tableData.columnsOrder.length === 0) {
            return columns;
        }

        const ordered: DataTableColumn<TRow>[] = [];
        const remaining = [...columns];

        // First, add columns in their stored order
        tableData.columnsOrder.forEach(order => {
            const index = remaining.findIndex(c => getColumnId(c) === order.field);
            if (index >= 0) {
                ordered.push(remaining[index]);
                remaining.splice(index, 1);
            }
        });

        // Add any new columns that weren't in the stored order
        ordered.push(...remaining);

        return ordered;
    }, [columns, tableData.columnsOrder]);

    // Extract text content from a ReactElement tree
    const extractTextFromElement = (element: React.ReactElement): string => {
        const children = element.props?.children;
        if (!children) return '';
        if (typeof children === 'string') return children;
        if (Array.isArray(children)) {
            return children.map(child => {
                if (typeof child === 'string') return child;
                if (React.isValidElement(child)) return extractTextFromElement(child);
                return '';
            }).join('');
        }
        if (React.isValidElement(children)) return extractTextFromElement(children);
        return '';
    };

    // Convert columns to sidebar format
    const sidebarColumns = useMemo((): ColumnsOptionsSidebarColumn<string>[] => {
        return orderedColumns.map(col => ({
            field: getColumnId(col),
            name: typeof col.name === 'string' ? col.name : extractTextFromElement(col.name)
        }));
    }, [orderedColumns]);

    const handleOrderChange = (orderData: OrderData<TRow>) => {
        setTableData(prev => ({ ...prev, order: orderData, page: 1 }));
        onOrderChange && onOrderChange(orderData);
    };

    const handleHiddenColumnsChange = (hiddenColumns: string[]) => {
        setTableData(prev => ({ ...prev, hiddenColumns }));
    };

    const handleColumnsOrderChange = (columnsOrder: ColumnsOptionsSidebarColumnOrder<string>[]) => {
        setTableData(prev => ({
            ...prev,
            columnsOrder: columnsOrder.map(o => ({ i: o.i, field: o.field }))
        }));
    };

    const handleColResize = (columnsWidth: ColumnWidth[]) => {
        setTableData(prev => ({ ...prev, columnsWidth }));
    };

    // Key to force DataTable re-mount on reset (ColumnResizer holds internal state)
    const [resetKey, setResetKey] = useState(0);

    const handleResetColumns = useCallback(() => {
        setTableData(prev => ({ ...prev, hiddenColumns: defaultHiddenColumns, columnsOrder: [], columnsWidth: [] }));
        setResetKey(prev => prev + 1);
    }, [defaultHiddenColumns]);

    // Excel export
    const [isExporting, setIsExporting] = useState(false);

    const handleExportExcel = useCallback(async () => {
        if (!exportExcel || isExporting) return;
        setIsExporting(true);
        try {
            if (exportExcel.customExport) {
                await exportExcel.customExport({
                    hiddenColumns: tableData.hiddenColumns,
                    orderedFields: orderedColumns.map(c => c.field),
                });
            } else {
                const allRows = await exportExcel.fetchAll();

                const visibleCols = orderedColumns
                    .filter(c => !tableData.hiddenColumns.includes(getColumnId(c)))
                    .filter(c => !c.excludeFromExport);

                const exportCols = visibleCols.map(col => ({
                    field: col.field,
                    name: typeof col.name === 'string' ? col.name : extractTextFromElement(col.name),
                    exportValue: col.exportValue,
                    exportName: col.exportName,
                    headerNote: col.headerNote,
                }));

                exportToExcel(allRows, exportCols, exportExcel.fileName);
            }
        } finally {
            setIsExporting(false);
        }
    }, [exportExcel, isExporting, orderedColumns, tableData.hiddenColumns]);

    useEffect(() => {
        if (exportRef) {
            exportRef.current = { exportExcel: handleExportExcel };
        }
        return () => { if (exportRef) exportRef.current = null; };
    }, [exportRef, handleExportExcel]);

    // Calculate toggle position to align with table header (centered vertically)
    const tableWrapperRef = useRef<HTMLDivElement>(null);
    const [toggleTopOffset, setToggleTopOffset] = useState(0);
    const SIDEBAR_FIXED_TOP = 75; // Must match the sidebar's top position in CSS (.sidebar { top: 75px })
    const TOGGLE_HEIGHT = 36; // Height of the toggle button

    const calculateToggleOffset = useCallback(() => {
        if (tableWrapperRef.current) {
            // Use getBoundingClientRect for accurate viewport-relative positioning.
            // Since the sidebar is position:fixed at top:75px, we need the viewport-relative
            // position of the table header to compute the toggle offset.
            const wrapperRect = tableWrapperRef.current.getBoundingClientRect();

            // Find table header height
            const headerRow = tableWrapperRef.current.querySelector('thead tr, th');
            const headerHeight = headerRow?.getBoundingClientRect().height ?? 40;

            // Center the toggle vertically on the header
            const headerCenterFromViewportTop = wrapperRect.top + (headerHeight / 2) + 15;
            const offset = Math.max(0, headerCenterFromViewportTop - SIDEBAR_FIXED_TOP - (TOGGLE_HEIGHT / 2));
            setToggleTopOffset(offset);
        }
    }, []);

    useEffect(() => {
        calculateToggleOffset();
        window.addEventListener('resize', calculateToggleOffset);

        // Recalculate after potential async layout shifts (e.g., counters loading above the table
        // after a tab switch, which push the table down after initial mount)
        const t1 = setTimeout(calculateToggleOffset, 300);
        const t2 = setTimeout(calculateToggleOffset, 600);

        return () => {
            window.removeEventListener('resize', calculateToggleOffset);
            clearTimeout(t1);
            clearTimeout(t2);
        };
    }, [calculateToggleOffset]);

    // Recalculate when rows change (data loading affects layout)
    useEffect(() => {
        const timeoutId = setTimeout(calculateToggleOffset, 100);
        return () => clearTimeout(timeoutId);
    }, [rows.length, calculateToggleOffset]);

    return (
        <div className={styles.container}>
            <div className={styles.tableWrapper} ref={tableWrapperRef}>
                <DataTable
                    key={resetKey}
                    columns={orderedColumns}
                    columnsWidths={tableData.columnsWidth}
                    rows={rows}
                    hiddenColumns={tableData.hiddenColumns}
                    order={tableData.order}
                    selectedRow={selectedRow}
                    disallowHover={disallowHover}
                    striped={striped}
                    showErrors={showErrors}
                    errors={errors}
                    className={className}
                    hideHeaderWhenNoData={hideHeaderWhenNoData}
                    colorRow={colorRow}
                    noItemsText={noItemsText}
                    infiniteScroll={infiniteScroll}
                    scrollToRowId={scrollToRowId}
                    onRowClick={onRowClick}
                    onOrder={handleOrderChange}
                    onHref={onHref}
                    rowStyle={rowStyle}
                    onRenderAction={onRenderAction}
                    onRowId={onRowId}
                    onColResize={handleColResize}
                    disableResize={disableResize}
                    expandableRow={expandableRow}
                />
            </div>
            <ColumnsOptionsSidebar
                columns={sidebarColumns}
                hiddenColumns={tableData.hiddenColumns}
                canDrag={canDragColumns}
                toggleTopOffset={toggleTopOffset}
                onChangeHiddenColumns={handleHiddenColumnsChange}
                onChangeColumnsOrder={handleColumnsOrderChange}
                onExportExcel={exportExcel && !hideExportInSidebar ? handleExportExcel : undefined}
                isExporting={isExporting}
                onReset={handleResetColumns}
            />
        </div>
    );
};

export default DataTableWithSidebar;
